import { z } from 'zod';
import {
  NonUKBankAccountReason,
  UKExemptSuppliesAnswer,
  UKPresenceType,
  UKVATRegistrationReason,
} from './formDataEnums';

// Cleared legacy fields are equivalent to absent optional fields.
const optional = <T extends z.ZodTypeAny>(schema: T) =>
  schema.nullish().transform((value) => value ?? undefined);

const draftText = z
  .string()
  .nullish()
  .transform((value) => value ?? '');
const optionalSelection = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) => (typeof value === 'string' && !value.trim() ? undefined : value),
    optional(schema)
  );

const addressSchema = z.object({
  addressLine1: draftText,
  addressLine2: draftText,
  addressLine3: draftText,
  postalCode: draftText,
  city: draftText,
  state: draftText,
  country: draftText,
});

const providerSchema = z.object({
  name: draftText,
  address: addressSchema
    .nullish()
    .transform((value) => value ?? addressSchema.parse({})),
});

export const registrationFormMetadataSchema = z.object({
  currency: optional(
    z
      .string()
      .trim()
      .toUpperCase()
      .refine(
        (value) => value.trim() === '' || /^[A-Z]{3}$/.test(value),
        'Expected a three-letter currency code'
      )
      .transform((value) => (value.trim() ? value : undefined))
  ),
  firstYearTaxableSale: optional(z.number().finite()),
  effectiveStartDateOfTaxAccount: optional(z.string()),
  accountingMethod: optional(z.string()),
  intraCommunityTrade: optional(z.boolean()),
  importsOrExportsGoods: optional(z.boolean()),
  reasonForNonUKBankAccount: optionalSelection(
    z.nativeEnum(NonUKBankAccountReason)
  ),
  ukVATRegistrationReasons: optional(
    z.array(z.nativeEnum(UKVATRegistrationReason))
  ),
  ukActivityDescription: optional(z.string()),
  goodsStoredOrShippedFromUK: optional(z.boolean()),
  ukGoodProviders: optional(z.array(providerSchema)),
  ukGoodsStorageProviderName: optional(z.string()),
  ukGoodsStorageAddress: optional(addressSchema),
  hasUKExemptSupplies: optionalSelection(z.nativeEnum(UKExemptSuppliesAnswer)),
  ukPresenceTypes: optional(z.array(z.nativeEnum(UKPresenceType))),
  ukPresenceDetails: optional(z.string()),
  directorsInvolvedInOtherUKBusiness: optional(z.boolean()),
  otherUKBusinessesDetails: optional(z.string()),
});
